import type {
  MobileFrontEndGalleryView,
  MobileFrontEndMapView,
  MobileUiConfigArtifact,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { ARTIFACT_ID_FIELD } from './constants';
import { galleryMediaFields, withCollectedAttachmentPreview } from './build-gallery-query';
import { isSelectableFieldPath } from './message-mapping';
import { isGraphQLName, toGraphQLLiteral } from './gql-literal';
import { buildFieldSelection } from './selection-set';
import { isGalleryView } from './view-registry';

/** Id-field candidates used as a stable point key (first present on the artifact wins). */
const ID_FIELD_CANDIDATES = [ARTIFACT_ID_FIELD, 'locationId', 'artifactId', 'id'];

/** Resolve the stable id field for map points, or ``null`` when none is selectable. */
export function mapPointIdField(artifact: MobileUiConfigArtifact | undefined): string | null {
  const names = new Set((artifact?.fields ?? []).map((f) => f.name));
  return ID_FIELD_CANDIDATES.find((candidate) => names.has(candidate)) ?? null;
}

/**
 * Fields to show in the point detail popover: the view's ``detail_fields`` (in
 * order) or, when empty, the label + timestamp fields.
 *
 * Authored ``detail_fields`` are filtered against introspected artifact fields
 * so a stale/mismatched name cannot break the whole map GraphQL query.
 */
export function mapDetailFieldNames(
  view: MobileFrontEndMapView,
  artifact: MobileUiConfigArtifact | undefined,
): string[] {
  const requested =
    view.detail_fields && view.detail_fields.length > 0
      ? view.detail_fields
      : [view.label_field, view.timestamp_field].filter((field): field is string => Boolean(field));
  if (!artifact?.fields?.length) return requested;
  return requested.filter((field) => isSelectableFieldPath(artifact, field));
}

/**
 * Gallery view whose file a pin should preview.
 *
 * Media artifacts (pictures, videos, Photos Media Information) author the file
 * on a sibling gallery, whether the coordinates are named ``exifLatitude`` or
 * plain ``latitude``. Maps with no gallery — cell towers, visits, trips — stay
 * text.
 */
export function mapPointMediaView(
  _view: MobileFrontEndMapView,
  artifact: MobileUiConfigArtifact | undefined,
): MobileFrontEndGalleryView | undefined {
  const gallery = artifact?.front_end?.views?.find(isGalleryView);
  return gallery ? withCollectedAttachmentPreview(gallery, artifact) : undefined;
}

/** Media URL / MIME paths the pin popup needs, when this map can preview a file. */
export function mapMediaPreviewFields(
  view: MobileFrontEndMapView,
  artifact: MobileUiConfigArtifact | undefined,
): string[] {
  const gallery = mapPointMediaView(view, artifact);
  if (!gallery) return [];
  const requested = galleryMediaFields(gallery);
  if (!artifact?.fields?.length) return requested;
  return requested.filter((field) => isSelectableFieldPath(artifact, field));
}

/** Union of every field the map view + point popover need to select. */
export function mapFieldSelection(
  view: MobileFrontEndMapView,
  artifact: MobileUiConfigArtifact | undefined,
  detailFields: string[],
): string {
  const fields = new Set<string>([view.latitude_field, view.longitude_field]);
  const idField = mapPointIdField(artifact);
  if (idField) fields.add(idField);
  if (view.label_field) fields.add(view.label_field);
  if (view.timestamp_field) fields.add(view.timestamp_field);
  for (const field of detailFields) fields.add(field);
  for (const field of mapMediaPreviewFields(view, artifact)) fields.add(field);
  // A trip authors several named pairs. The map view itself only names one of
  // them, so the other ends have to be selected or they never reach the pins.
  const locations = artifact?.locations ?? [];
  if (locations.length >= 2) {
    for (const location of locations) {
      if (!artifact?.fields?.length || isSelectableFieldPath(artifact, location.latitude)) {
        fields.add(location.latitude);
      }
      if (!artifact?.fields?.length || isSelectableFieldPath(artifact, location.longitude)) {
        fields.add(location.longitude);
      }
    }
  }
  return buildFieldSelection(fields);
}

/** Resolve the plural root query for the map artifact itself. */
export function mapRootQuery(artifact: MobileUiConfigArtifact | undefined): string | null {
  const name = artifact?.front_end?.graphql_root_query;
  return name && isGraphQLName(name) ? name : null;
}

/** Build one page of a map-point list query. Args are inlined as GraphQL literals. */
export function buildMapPointsQuery(args: {
  rootQuery: string;
  mobileAcquisitionId: number;
  offset: number;
  limit: number;
  selection: string;
  where?: Record<string, unknown> | null;
}): string | null {
  if (!isGraphQLName(args.rootQuery)) return null;
  const argParts = [
    `mobileAcquisitionId: ${args.mobileAcquisitionId}`,
    `offset: ${args.offset}`,
    `limit: ${args.limit}`,
  ];
  if (args.where && Object.keys(args.where).length > 0) {
    argParts.push(`where: ${toGraphQLLiteral(args.where)}`);
  }
  return `query MapPoints {
  ${args.rootQuery}(${argParts.join(', ')}) {
    total
    offset
    limit
    items {
        ${args.selection}
    }
  }
}`;
}
